/**
 * Change log (OP#1930) -- who changed what, when, before/after, per site and per device.
 *
 * The field equivalent of Aruba Central "Config Audit" / Meraki "Change log", backed by
 * GET /api/v2/audit (api/v2/audit_log.py). Until OP#1930 this page was a stub.
 *
 * What the SERVER guarantees (so this page never has to): tenancy and site confinement (a
 * site_operator / read_only sees only their buildings), which actions a role may see at all
 * (customers never see access denials, AI internals, team changes or platform rows), and that
 * every value is scrubbed (no PSK, shared secret, Vault ref, token or raw webhook target).
 * The CSV export is built from the rows on screen, so it carries exactly what the grid shows.
 *
 * Read-only: no mutating control lives here. Scope comes from the header (customer / site);
 * the in-page filters narrow inside it. Times are shown in UTC so a screenshot and an export
 * agree. platform_admin also sees the per-customer hash-chain verification status.
 */
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageHeader, EmptyState } from "@/components/ui/Page";
import { DataGrid } from "@/components/data/DataGrid";
import { DiffView } from "@/components/data/DiffView";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { useSession } from "@/stores/session";
import { useAuditChain, useAuditLog } from "@/lib/api/auditLog";
import {
  ACTION_FAMILIES, MSP_ACTION_FAMILIES, actionLabel, fmtUtc, outcomeRamp, targetText, toCsv,
  type AuditEntry, type AuditFilters,
} from "@/lib/auditView";

// Same filter control look as the Devices list (tokens only, so both themes follow).
const filterCls = "rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink";

export default function Audit() {
  const isMsp = useSession((s) => s.hasRole("msp_admin"));
  const isPlatform = useSession((s) => s.effectiveRole() === "platform_admin");
  const siteName = useSession((s) => s.currentSite()?.name ?? null);

  // Draft = what the inputs hold; applied = what was last queried. Text filters apply on
  // submit (Enter / Apply) so typing an actor name does not fire a request per keystroke.
  const [draft, setDraft] = useState<AuditFilters>({});
  const [applied, setApplied] = useState<AuditFilters>({});
  const log = useAuditLog(applied);
  const chain = useAuditChain(isPlatform);

  const rows = useMemo(() => (log.data?.pages ?? []).flatMap((p) => p.items), [log.data]);
  const total = log.data?.pages[0]?.total ?? 0;
  const families = isMsp ? [...ACTION_FAMILIES, ...MSP_ACTION_FAMILIES] : ACTION_FAMILIES;
  const filtering = Object.values(applied).some((v) => (v ?? "").trim() !== "");

  const set = (k: keyof AuditFilters) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const apply = (next: AuditFilters = draft) => setApplied({ ...next });
  const clear = () => {
    setDraft({});
    setApplied({});
  };

  /** Download the VISIBLE rows (filters applied, loaded pages only) as CSV. */
  const exportCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `change-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo<ColumnDef<AuditEntry, unknown>[]>(
    () => [
      {
        id: "time",
        header: "Time (UTC)",
        accessorFn: (r) => r.created_at ?? "",
        cell: (c) => (
          <span className="whitespace-nowrap tabular-nums text-ink-muted">{fmtUtc(c.row.original.created_at)}</span>
        ),
      },
      {
        id: "actor",
        header: "Actor",
        accessorFn: (r) => r.actor_name ?? r.actor_type,
        cell: (c) => <ActorCell row={c.row.original} />,
      },
      {
        id: "action",
        header: "Action",
        accessorFn: (r) => actionLabel(r.action),
        cell: (c) => <ActionCell row={c.row.original} />,
      },
      {
        id: "target",
        header: "Target",
        accessorFn: (r) => targetText(r),
        cell: (c) => <TargetCell row={c.row.original} />,
      },
      {
        id: "summary",
        header: "Summary",
        enableSorting: false,
        cell: (c) => <SummaryCell row={c.row.original} />,
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Change log"
        subtitle={
          siteName
            ? `Who changed what at ${siteName}, and when — append-only`
            : "Who changed what, and when — append-only"
        }
        actions={
          <Button onClick={exportCsv} disabled={rows.length === 0} data-testid="audit-export">
            Export CSV
          </Button>
        }
      />

      {isPlatform && chain.data && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="audit-chain">
          <StatusPill status={chain.data.ok ? "healthy" : "critical"}>
            {chain.data.ok ? "Hash chain intact" : "Hash chain broken"}
          </StatusPill>
          <span className="text-ink-muted">
            {chain.data.checked} rows checked
            {!chain.data.ok && chain.data.broken_at !== null
              ? ` · first break at row ${chain.data.broken_at} (${chain.data.reason})`
              : ""}
          </span>
        </div>
      )}

      <form
        className="md-card mb-3 flex flex-wrap items-end gap-2 bg-surface p-3"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
        aria-label="Change log filters"
      >
        <select
          className={filterCls}
          aria-label="Action"
          value={draft.action ?? ""}
          onChange={(e) => {
            // A select is a deliberate choice: apply it immediately.
            const next = { ...draft, action: e.target.value };
            setDraft(next);
            apply(next);
          }}
        >
          <option value="">All changes</option>
          {families.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <input
          className={filterCls}
          aria-label="Device serial"
          placeholder="Device serial"
          value={draft.serial ?? ""}
          onChange={(e) => set("serial")(e.target.value)}
        />
        <input
          className={filterCls}
          aria-label="Actor"
          placeholder="Actor (name or email)"
          value={draft.actor ?? ""}
          onChange={(e) => set("actor")(e.target.value)}
        />
        <label className="flex items-center gap-1 text-sm text-ink-muted">
          From
          <input
            type="date"
            className={filterCls}
            aria-label="From date"
            value={draft.from ?? ""}
            onChange={(e) => set("from")(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-sm text-ink-muted">
          To
          <input
            type="date"
            className={filterCls}
            aria-label="To date"
            value={draft.to ?? ""}
            onChange={(e) => set("to")(e.target.value)}
          />
        </label>
        <Button type="submit" variant="primary">
          Apply
        </Button>
        {filtering && (
          <Button variant="ghost" onClick={clear}>
            Clear filters
          </Button>
        )}
      </form>

      {log.isError ? (
        <EmptyState
          title="The change log could not be loaded"
          hint={
            (log.error as { status?: number } | null)?.status === 404
              ? "That site or device is not in your scope."
              : "Try again in a moment."
          }
        />
      ) : (
        <>
          <div className="mb-2 text-sm text-ink-muted" data-testid="audit-count">
            {log.isLoading ? "Loading…" : `Showing ${rows.length} of ${total} changes`}
          </div>
          <DataGrid
            data={rows}
            columns={columns}
            rowKey={(r) => String(r.id)}
            renderCard={(r) => <AuditCard row={r} />}
            isLoading={log.isLoading}
            empty={filtering ? "No changes match these filters." : "No changes recorded in this scope yet."}
          />
          {log.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage}>
                {log.isFetchingNextPage ? "Loading…" : "Load older changes"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---- cells ------------------------------------------------------------------------------- */

function ActorCell({ row }: { row: AuditEntry }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-ink">{row.actor_name ?? row.actor_type}</div>
      {row.actor_type !== "operator" && (
        <div className="text-xs text-ink-subtle">{row.actor_type}</div>
      )}
    </div>
  );
}

function ActionCell({ row }: { row: AuditEntry }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-ink">{actionLabel(row.action)}</span>
      <span className="flex items-center gap-2">
        {row.outcome !== "success" && (
          <StatusPill status={outcomeRamp(row.outcome)}>{row.outcome}</StatusPill>
        )}
        <code className="text-xs text-ink-subtle">{row.action}</code>
      </span>
    </div>
  );
}

function TargetCell({ row }: { row: AuditEntry }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-ink">{targetText(row)}</div>
      {row.site_name && <div className="text-xs text-ink-subtle">{row.site_name}</div>}
    </div>
  );
}

/** Summary line plus the expandable before/after (the shared DiffView, review-only). */
function SummaryCell({ row }: { row: AuditEntry }) {
  const n = row.changes.length;
  return (
    <div className="min-w-0 max-w-xl">
      <div className="text-ink">{row.summary || "—"}</div>
      {n > 0 && (
        <details className="mt-1" data-testid="audit-diff">
          <summary className="cursor-pointer text-sm text-brand hover:underline">
            Before / after ({n}
            {row.changes_truncated ? "+" : ""} {n === 1 ? "change" : "changes"})
          </summary>
          <div className="mt-2">
            <DiffView
              diff={{
                summary: row.changes_truncated
                  ? `First ${n} changes shown (the full list is longer)`
                  : `${n} ${n === 1 ? "change" : "changes"}`,
                changes: row.changes,
              }}
            />
          </div>
        </details>
      )}
    </div>
  );
}

/** Sub-lg card (tablet reflow): the same fields, stacked. */
function AuditCard({ row }: { row: AuditEntry }) {
  return (
    <div className="flex flex-col gap-1 bg-surface">
      <div className="flex items-start justify-between gap-2">
        <ActionCell row={row} />
        <span className="whitespace-nowrap text-xs tabular-nums text-ink-muted">{fmtUtc(row.created_at)}</span>
      </div>
      <div className="text-sm text-ink-muted">
        {row.actor_name ?? row.actor_type} · {targetText(row)}
        {row.site_name ? ` · ${row.site_name}` : ""}
      </div>
      <SummaryCell row={row} />
    </div>
  );
}
